import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Stethoscope, MessageSquare, Clock, CheckCircle2, AlertCircle, Eye } from 'lucide-react';
import EngineerSidebar from '../Sidebar/EngineerSidebar';
import { useToast } from '../../hooks/use-toast';
import { consultingAPI } from '../../api';

const EngineerConsultings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [consultings, setConsultings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadConsultings();
  }, []);

  const loadConsultings = async () => {
    try {
      const data = await consultingAPI.getConsultings();
      setConsultings(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les consultings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Terminé</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30"><Clock className="h-3 w-3 mr-1" />En cours</Badge>;
      case 'pending_payment':
        return <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30"><AlertCircle className="h-3 w-3 mr-1" />En attente paiement</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30"><AlertCircle className="h-3 w-3 mr-1" />En attente</Badge>;
    }
  };

  const getPaymentBadge = (paymentStatus) => {
    return paymentStatus === 'paid' 
      ? <Badge className="bg-green-500/20 text-green-500 border-green-500/30">Payé</Badge>
      : <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30">En attente</Badge>;
  };

  const filteredConsultings = consultings.filter(consulting => {
    if (filter === 'all') return true;
    if (filter === 'paid') return consulting.payment_status === 'paid';
    if (filter === 'in_progress') return consulting.status === 'in_progress';
    if (filter === 'completed') return consulting.status === 'completed';
    return true;
  });

  return (
    <div className="min-h-screen bg-[#111111]">
      <EngineerSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              <span className="text-[#C00000]">Consultings</span> Diagnostic
            </h1>
            <p className="text-[#A6A6A6]">Gérez toutes les demandes de consulting</p>
          </div>

          {/* Filters */}
          <div className="flex gap-3 mb-6">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              className={filter === 'all' ? 'bg-[#C00000] hover:bg-[#C00000]/90' : 'border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10'}
              onClick={() => setFilter('all')}
            >
              Tous ({consultings.length})
            </Button>
            <Button
              variant={filter === 'paid' ? 'default' : 'outline'}
              className={filter === 'paid' ? 'bg-[#C00000] hover:bg-[#C00000]/90' : 'border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10'}
              onClick={() => setFilter('paid')}
            >
              Payés ({consultings.filter(c => c.payment_status === 'paid').length})
            </Button>
            <Button
              variant={filter === 'in_progress' ? 'default' : 'outline'}
              className={filter === 'in_progress' ? 'bg-[#C00000] hover:bg-[#C00000]/90' : 'border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10'}
              onClick={() => setFilter('in_progress')}
            >
              En cours ({consultings.filter(c => c.status === 'in_progress').length})
            </Button>
            <Button
              variant={filter === 'completed' ? 'default' : 'outline'}
              className={filter === 'completed' ? 'bg-[#C00000] hover:bg-[#C00000]/90' : 'border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10'}
              onClick={() => setFilter('completed')}
            >
              Terminés ({consultings.filter(c => c.status === 'completed').length})
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#A6A6A6]">Chargement...</p>
            </div>
          ) : filteredConsultings.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="py-12">
                <div className="text-center">
                  <Stethoscope className="h-12 w-12 text-[#A6A6A6] mx-auto mb-4" />
                  <p className="text-[#A6A6A6]">Aucun consulting trouvé</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredConsultings.map((consulting) => (
                <Card key={consulting.consulting_id} className="bg-[#1a1a1a] border-[#A6A6A6]/20 hover:border-[#C00000]/50 transition-colors">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-white text-xl mb-2 flex items-center">
                          <Stethoscope className="h-5 w-5 mr-2 text-[#C00000]" />
                          {consulting.consulting_id}
                        </CardTitle>
                        <div className="space-y-1">
                          <p className="text-[#A6A6A6] text-sm">
                            <strong className="text-white">Client:</strong> {consulting.client_name}
                          </p>
                          <p className="text-[#A6A6A6] text-sm">
                            <strong className="text-white">Véhicule:</strong> {consulting.vehicle_info.brand} {consulting.vehicle_info.model} {consulting.vehicle_info.engine} ({consulting.vehicle_info.year})
                          </p>
                          <p className="text-[#A6A6A6] text-sm line-clamp-2">
                            <strong className="text-white">Problème:</strong> {consulting.problem}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-end ml-4">
                        {getStatusBadge(consulting.status)}
                        {getPaymentBadge(consulting.payment_status)}
                        <span className="text-[#C00000] font-bold">{consulting.price}€</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-3">
                      <Button
                        className="bg-[#C00000] hover:bg-[#C00000]/90 text-white"
                        onClick={() => navigate(`/engineer/consultings/${consulting.consulting_id}`)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Voir le détail
                      </Button>
                      <Button
                        variant="outline"
                        className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                        onClick={() => navigate(`/engineer/consultings/${consulting.consulting_id}`)}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Chat
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EngineerConsultings;
